import {Event as Event_V3} from '@wix/ambassador-events-v3-event/types'
import {useStyles} from '@wix/tpa-settings/react'
import {IMAGE_HOVER_EFFECT, getEventDescription, getEventTitle} from '@wix/wix-events-commons-statics'
import {useEnvironment, useTranslation} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React, {useMemo, useRef, useState} from 'react'
import stylesParams from '../../../../stylesParams'
import {useWidgetState} from '../../../hooks/state-provider'
import {useImageHoverEffect} from '../../../hooks/use-image-hover-effect'
import {useVisibilityStyles} from '../../../hooks/use-visibility-styles'
import {getEventBadge} from '../../../selectors/events'
import {shouldShowCountdown} from '../../../selectors/layout'
import {isMembersVisible, isRibbonVisible} from '../../../selectors/settings'
import classesV2 from '../../classes-v2.scss'
import {Countdown} from '../../countdown'
import {EventBadge} from '../../event-badge'
import {EventImage} from '../../event-image'
import {FullDateLocation} from '../../full-date-location'
import {LinkToPage} from '../../link-to-page'
import {Members} from '../../members'
import {Ribbon} from '../../ribbon'
import {RsvpButton} from '../../rsvp-button'
import {ShortDateLocation} from '../../short-date-location'
import {SocialBar} from '../../social-bar'
import s from './side-by-side-item.scss'

const getImageHoverEffectClass = (imageHoverEffect: IMAGE_HOVER_EFFECT) => {
  if (imageHoverEffect === IMAGE_HOVER_EFFECT.ZOOM_IN) {
    return s.zoomInEffect
  }

  return null
}

const initialDetailsStyles = {
  scrollContainer: {},
  content: {},
}

interface SideBySideItemProps {
  event: ExtendedEvent | Event_V3
  evenItem: boolean
}

export const SideBySideItem = ({event, evenItem}: SideBySideItemProps) => {
  const {t} = useTranslation()
  const {isRTL} = useEnvironment()
  const {get} = useStyles()

  const {effectPreviewActive, imageHoverEffect} = useImageHoverEffect()
  const imageHoverEffectClassName = useMemo(() => getImageHoverEffectClass(imageHoverEffect), [imageHoverEffect])

  const showMembers = useWidgetState(state => isMembersVisible(state, event))
  const showRibbon = useWidgetState(state => isRibbonVisible(state, event))
  const showCountdown = shouldShowCountdown(event)
  const listItemLeftPadding = get(stylesParams.listItemLeftPadding)
  const listItemRightPadding = get(stylesParams.listItemRightPadding)

  const scrollableContainerRef = useRef<HTMLDivElement>(null)
  const [scrollableContainerStyles, setScrollableContainerStyles] = useState(initialDetailsStyles)

  const {
    allBreakpoints: {
      isListCountdownEnabled,
      isListShortDateLocationVisible,
      isListFullDateLocationVisible,
      isListDescriptionEnabled,
      isEventBadgeOnImage,
      isEventBadgeInEventInfo,
    },
  } = useVisibilityStyles()

  const eventBadge = useWidgetState(state => getEventBadge(state, event))

  const handleMouseOver = () => {
    const scrollableContainerWidth = scrollableContainerRef.current.getBoundingClientRect().width

    setScrollableContainerStyles({
      scrollContainer: {
        overflow: 'auto',
      },
      content: {
        width: scrollableContainerWidth - (isRTL ? listItemLeftPadding : listItemRightPadding),
        padding: 0,
        paddingInlineStart: isRTL ? listItemRightPadding : listItemLeftPadding,
      },
    })
  }

  const handleMouseOut = () => setScrollableContainerStyles(initialDetailsStyles)

  return (
    <div
      className={classNames(s.container, evenItem ? s.even : s.odd, {
        [s.forceImageHoverEffect]: effectPreviewActive,
      })}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
    >
      <div className={s.imageContainer}>
        <EventImage
          opacityFallback
          backgroundFallback
          event={event}
          className={classNames(s.image, imageHoverEffectClassName)}
        />
        {isEventBadgeOnImage(eventBadge?.type) && <EventBadge onImage event={event} />}
      </div>
      <div className={s.content}>
        <div className={classNames(s.details, s.widgetHeightSettings)}>
          {isEventBadgeInEventInfo(eventBadge?.type) && (
            <EventBadge
              event={event}
              className={classNames(s.eventBadge, {[s.overrideMargin]: showRibbon || showCountdown})}
            />
          )}
          {showRibbon && (
            <Ribbon event={event} className={classNames(s.ribbon, {[s.overrideMargin]: !showCountdown})} />
          )}
          {isListCountdownEnabled() && showCountdown && (
            <div className={s.countdown}>
              <Countdown event={event} />
            </div>
          )}
          <div
            className={s.scrollContainer}
            ref={scrollableContainerRef}
            style={scrollableContainerStyles.scrollContainer}
          >
            <div className={s.scrollContent} style={scrollableContainerStyles.content}>
              <div className={s.title}>
                <LinkToPage allowExternal event={event} dataHook="title">
                  {getEventTitle(event)}
                </LinkToPage>
              </div>
              {isListShortDateLocationVisible() && (
                <div className={s.shortDateLocation}>
                  <ShortDateLocation
                    event={event}
                    dateClassName={classesV2.eventDate}
                    locationClassName={classesV2.eventLocation}
                  />
                </div>
              )}
              {isListFullDateLocationVisible() && (
                <div className={s.fullDateLocation}>
                  <FullDateLocation
                    event={event}
                    dateClassName={classesV2.eventDate}
                    locationClassName={classesV2.eventLocation}
                  />
                </div>
              )}
              {!!getEventDescription(event) && isListDescriptionEnabled() && (
                <div className={s.description}>{getEventDescription(event)}</div>
              )}
              {showMembers && (
                <div className={s.members}>
                  <Members event={event} />
                </div>
              )}
              <SocialBar event={event} t={t} className={s.socialBar} />
            </div>
          </div>
        </div>
        <div className={s.rsvpButton}>
          <RsvpButton event={event} />
        </div>
      </div>
    </div>
  )
}
